import * as THREE from 'three';
import type { Chapter, ChapterContext } from './types';
import { LETTER } from '../data/letter';
import { EARTH, bendRadians, evaluateShot, radToDeg, type Shot } from '../render/sun/geodesic';
import type { ScreenLabel } from './types';
import { createEarth, createStars, createSun } from '../render/sun/bodies';
import { LightLattice } from '../render/sun/lattice';
import { TickField } from '../render/sun/tickField';
import { REVEAL_PER_SECOND, ShotView } from '../render/sun/shot';

const META = LETTER.chapters.find((c) => c.id === 'deeper')!;

/** the Sun's well really does slow a clock at its surface by about 2.1 millionths: that is the rate the clocks run at here */
const RATE = 1 - 2.1e-6;
/** seconds of mission time per real second: an hour, so the clocks visibly turn */
const MISSION_SECONDS_PER_SECOND = 3600;

const TARGET = new THREE.Vector3(0.5, 0, 0);
const START_B = 2.2;
const SLIDER = { min: 1.2, max: 3.5, step: 0.05 };
/** a short beat after both paths are drawn, in points at the reveal pace, before the verdict */
const SETTLE_POINTS = 64;

type Phase = 'idle' | 'aim' | 'flying' | 'done';

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Chapter one, "Deeper": the Sun's well. A lattice of light threads bends toward the Sun, a field of ticking markers
 * slows toward it, and the player lowers or raises a ray of starlight until it bends into Earth's telescope.
 * Units: Sun radius 1, GM 1, c^2 8 (gravity exaggerated about 60,000 times).
 */
export class SunChapter implements Chapter {
  readonly id = 'deeper';
  readonly title = 'Deeper';
  readonly description = 'The Sun with a grid of light paths bending around it; a slider sets how close a beam of starlight passes, and a telescope on Earth waits to the right.';
  private readonly labelV = new THREE.Vector3();
  private lastW = 0; private lastH = 0;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(48, 16 / 9, 0.1, 400);

  private ctx!: ChapterContext;
  private phase: Phase = 'idle';
  private b = START_B;
  private time = 0;
  private idle = 0;
  private shotT = 0;
  private flash = 0;
  private current: Shot | null = null;
  private earthNote = 'ready';
  private reduceMotion = false;
  private orbit = { az: -1.9, el: 0.3, radius: 17, radiusTarget: 17 };

  private sun = createSun();
  private earth = createEarth();
  private lattice = new LightLattice();
  private ticks = new TickField();
  private view = new ShotView();

  constructor() {
    this.scene.background = new THREE.Color(0x03050c);
    this.camera.up.set(0, 0, 1);
    this.scene.add(createStars(), this.ticks.points, this.lattice.group, this.sun.group, this.earth.group, this.view.group);
    this.applyCamera(1);
  }

  enter(ctx: ChapterContext) {
    this.ctx = ctx;
    this.phase = 'aim';
    this.b = START_B;
    this.time = 0; this.idle = 0; this.shotT = 0; this.flash = 0; this.current = null;
    this.earthNote = 'ready';
    this.reduceMotion = reducedMotion();
    this.orbit = { az: -1.9, el: 0.3, radius: 17, radiusTarget: 17 };
    this.view.clear();
    this.view.setHeight(this.b);
    this.earth.setFlash(0);
    this.applyCamera(this.camera.aspect);

    ctx.say(META.intro);
    ctx.hud.setLever({
      kind: 'slider', label: 'Starlight height', min: SLIDER.min, max: SLIDER.max, step: SLIDER.step, value: START_B,
      format: (v) => v.toFixed(2) + ' Sun-radii',
      onInput: (v) => this.setHeight(v),
      action: { label: 'Send the light', onClick: () => this.send() }
    });
    ctx.hud.setMove(META.lever);
    ctx.hud.setHint('');
    this.refreshReadouts();
  }

  exit() {
    this.view.clear();
    this.current = null;
    this.phase = 'idle';
    if (!this.ctx) return;
    this.ctx.hud.setLever(null);
    this.ctx.hud.setMove(null);
    this.ctx.hud.setHint('');
  }

  onDrag(dx: number, dy: number) {
    this.orbit.az -= dx * 0.006;
    this.orbit.el = clamp(this.orbit.el + dy * 0.005, -0.6, 1.2);
    this.idle = 0;
  }

  onWheel(deltaY: number) {
    this.orbit.radiusTarget = clamp(this.orbit.radiusTarget * (1 + deltaY * 0.001), 6, 40);
    this.idle = 0;
  }

  onClick(_x: number, _y: number) { /* the slider is the lever; clicks on the canvas do nothing */ }

  /** The accessible route: set a height that lands and send the light. */
  demonstrate() {
    if (!this.ctx || this.phase !== 'aim') return;
    this.setHeight(1.65);
    this.ctx.hud.setLeverValue(1.65);
    this.ctx.hud.setHint('Watch the gold line: the real path. The dashes are what a plain pull would do.');
    this.send();
  }

  /** The Sun and the telescope, named where they are. */
  labels(width: number, height: number): ScreenLabel[] {
    const out: ScreenLabel[] = [];
    const put = (text: string, x: number, y: number, z: number, dy: number) => {
      const v = this.labelV.set(x, y, z).project(this.camera);
      if (v.z > 1) return;
      out.push({ text, x: (v.x + 1) / 2 * width, y: (1 - v.y) / 2 * height + dy });
    };
    put('The Sun', 0, 0, 0, 34);
    put('Earth, with the telescope', EARTH.x, EARTH.y, 0, 14);
    return out;
  }

  update(dt: number, _t: number, width: number, height: number) {
    if (!this.ctx) return;
    dt = clamp(dt, 0, 0.1);
    this.time += dt; this.idle += dt;
    const { clock, audio } = this.ctx;

    clock.advance(dt * MISSION_SECONDS_PER_SECOND, dt * MISSION_SECONDS_PER_SECOND * RATE);
    audio.setScene({ rate: RATE, dist: 60, phase: 'deeper', orbitPhase: 0 });

    // camera: drift when idle, ease the zoom, keep the aspect right
    const o = this.orbit;
    if (this.idle > 1.5 && !this.reduceMotion) o.az += 0.05 * dt;
    o.radius += (o.radiusTarget - o.radius) * (1 - Math.exp(-dt * 8));
    const aspect = width / Math.max(1, height);
    this.applyCamera(aspect);
    if (width !== this.lastW || height !== this.lastH) {
      this.lastW = width; this.lastH = height;
      const lift = height > width * 1.05 ? 0.13 : 0;   // a phone held upright: the words sit low, so the Sun rides high
      if (lift) this.camera.setViewOffset(width, height, 0, lift * height, width, height); else this.camera.clearViewOffset();
    }

    // the picture
    this.sun.update(this.time);
    this.lattice.update(this.time);
    this.ticks.update(this.time, height / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))));

    if (this.phase === 'flying' && this.current) {
      this.shotT += dt;
      const k = Math.floor(this.shotT * REVEAL_PER_SECOND);
      this.view.reveal(k);
      if (k >= this.view.total + SETTLE_POINTS) this.resolve(this.current);
    }
    if (this.flash > 0) { this.flash = Math.max(0, this.flash - dt * 0.6); this.earth.setFlash(this.flash); }
  }

  /** Place the camera on its orbit around the target. On a narrow screen it stands further back so the whole lattice fits. */
  private applyCamera(aspect: number) {
    const cam = this.camera;
    if (Math.abs(cam.aspect - aspect) > 1e-6) { cam.aspect = aspect; cam.updateProjectionMatrix(); }
    const o = this.orbit, fit = Math.min(3.4, Math.max(1, 1.5 / aspect)), r = Math.min(150, o.radius * fit), ce = Math.cos(o.el);
    cam.position.set(TARGET.x + r * ce * Math.cos(o.az), TARGET.y + r * ce * Math.sin(o.az), TARGET.z + r * Math.sin(o.el));
    cam.up.set(0, 0, 1);
    cam.lookAt(TARGET);
  }

  /** The slider moved: the launch handle follows, and any ray still on screen is cleared. */
  private setHeight(v: number) {
    this.b = v;
    this.view.setHeight(v);
    if (this.phase === 'flying' || this.current || this.earthNote !== 'ready') {
      this.view.clear();
      this.current = null;
      if (this.phase === 'flying') { this.phase = 'aim'; this.ctx.hud.setHint(''); }
      this.earthNote = 'ready';
    }
    this.refreshReadouts();
  }

  /** Send the light: compute both paths, then let `update` draw them. */
  private send() {
    if (this.phase !== 'aim') return;
    const shot = evaluateShot(this.b);
    this.current = shot;
    this.shotT = 0;
    this.phase = 'flying';
    this.view.launch(shot);
    this.earthNote = 'light in flight';
    this.ctx.hud.setHint(`Light on its way from ${this.b.toFixed(2)} Sun-radii out. The gold line is the real path; the dashes are what a plain pull would do.`);
    this.ctx.audio.event('dive');
    this.refreshReadouts();
  }

  /** Both paths are drawn: judge the shot. */
  private resolve(shot: Shot) {
    this.current = null;
    const { hud, audio } = this.ctx;
    if (shot.hit) {
      this.phase = 'done';
      this.flash = 1;
      this.earthNote = 'starlight received';
      this.ctx.say(META.payoff);
      this.ctx.measure('deeper', LETTER.measurements.deeper);
      audio.blip('ok');
      hud.setLever(null);
      hud.setMove(null);
      hud.setHint(`Starlight from ${shot.b.toFixed(2)} Sun-radii out landed in the telescope.`);
      this.refreshReadouts();
      this.ctx.finish();
      return;
    }
    this.phase = 'aim';
    let tip: string;
    switch (shot.verdict) {
      case 'sun':
        tip = 'Too close. That one fell into the Sun. Slide it higher and try again.';
        this.earthNote = 'light lost in the Sun';
        break;
      case 'newton-hits':
        tip = 'A plain pull would have hit, but real gravity bends light more than that. Slide it a bit higher.';
        this.earthNote = `missed by ${shot.missEinstein.toFixed(1)} Sun-radii`;
        break;
      case 'too-bent':
        tip = 'It bent too much and swung past Earth. Slide it higher.';
        this.earthNote = `missed by ${shot.missEinstein.toFixed(1)} Sun-radii`;
        break;
      default:
        tip = 'It barely bent. Light bends more the closer it passes the Sun. Slide it lower.';
        this.earthNote = `missed by ${shot.missEinstein.toFixed(1)} Sun-radii`;
    }
    hud.setHint(tip);
    audio.blip('warn');
    this.refreshReadouts();
  }

  private refreshReadouts() {
    const deg = radToDeg(bendRadians(this.b));
    this.ctx.hud.setReadouts([
      ['Starlight height', `${this.b.toFixed(2)} Sun-radii`],
      ['Bend here, gravity turned up 60,000×', `${deg.toFixed(1)}°`],
      ['A plain pull would give', `${(deg / 2).toFixed(1)}°`],
      ['The real Sun, at its edge', '1.75 arcseconds'],
      ['Earth, with the telescope', this.earthNote]
    ]);
  }
}
